// Note Review "Visit" and "Provider" helpers. Pure (no React, no network).
// Test: node --no-warnings src/notereview/noteVisit.test.mjs
//
// A note is filed to one appointment (recordings.config.appointmentid).
// Sign & Post posts it to THAT appointment's Athena encounter, and Note
// Review shows it under that appointment's provider. Patient 8823 (09-29):
// today's recording was filed to the 09-08 visit, so it showed under
// Dr. Adupa and would have signed into the 09-08 encounter. "Visit" moves an
// unsigned note to the right appointment (BlinkScribe
// PUT /v1/recording/{id}/visit); "Provider" overrides the signing provider
// when the server supports it.

import { startDateKey } from "../landingscreen/cards/visitflow.js";
import { shortDate, visitTimeLabel, hasAppointmentId } from "../utils/visitGuard.js";

// Signed (and posted): never movable, never re-assignable.
export const isNoteSigned = (note) =>
  String(note?.transcriptionstatus ?? "").trim().toUpperCase() === "APPROVED" ||
  Boolean(note?.signed_at);

// "YYYY-MM-DD" from "2026-09-29", "2026-09-29T09:21:14.5", "2026-09-29 09:21",
// or "09/29/2026"; "" when unknown.
export const dayKeyOf = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return "";
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);
  if (us) return `${us[3]}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(s);
  if (m) return m[1];
  return startDateKey(s) || "";
};

// The day the note was recorded: BlinkScribe's recording_date (the audio
// timestamp in clinic time, the day its server checks use), else the row's
// clinic-local ts.
export const recordingDayKey = (note) => dayKeyOf(note?.recording_date) || dayKeyOf(note?.ts);

// The day of the visit the note is filed to.
export const noteVisitDayKey = (note) => dayKeyOf(note?.startdate);

// Recorded on a different day than the visit it is filed to.
export const noteVisitMismatch = (note) => {
  const rec = recordingDayKey(note);
  const appt = noteVisitDayKey(note);
  return Boolean(rec && appt && rec !== appt);
};

const joinDot = (...parts) => parts.filter(Boolean).join(" · ");

// "09-08 · 8:20 AM · Reshmi Adupa, MD"
export const noteVisitLabel = (note) =>
  joinDot(
    shortDate(noteVisitDayKey(note)),
    visitTimeLabel({ starttime: note?.starttime }),
    String(note?.providername ?? "").trim(),
  ) || (hasAppointmentId(note?.appointmentid) ? `Visit #${note.appointmentid}` : "No visit");

// One visit-options entry -> normalised { appointmentid, dayKey, label, ... }.
// BlinkScribe's entries: { appointmentid, date "YYYY-MM-DD", time "HH:MM",
// provider, visit_confirmation, is_current, is_recording_day, active,
// notes: [{ id, signed, transcriptionstatus, ... }] } (notes = OTHER notes
// already on that appointment).
const normOption = (o) => {
  if (!o || !hasAppointmentId(o.appointmentid)) return null;
  const dayKey = dayKeyOf(o.date ?? o.startdate);
  const time = visitTimeLabel({ starttime: o.time ?? o.starttime });
  const provider = String(o.provider ?? o.providername ?? "").trim();
  const status = String(o.visit_confirmation ?? "").trim();
  const notes = Array.isArray(o.notes) ? o.notes.filter(Boolean) : [];
  return {
    appointmentid: String(Number(o.appointmentid)),
    dayKey,
    time,
    provider,
    status,
    label: joinDot(shortDate(dayKey) || dayKey, time, provider, status),
    serverCurrent: o.is_current === true,
    serverRecordingDay: o.is_recording_day === true,
    signedNoteIds: notes.filter((n) => n.signed).map((n) => n.id),
    unsignedNoteIds: notes.filter((n) => !n.signed).map((n) => n.id),
  };
};

// Blockers the server reports for the note itself (the move is refused).
const BLOCKER_TEXT = {
  NOTE_SIGNED: "This note is signed; a signed note never moves.",
  NOTE_SUPERSEDED: "This row was combined into a newer note; move that note instead.",
  NOTE_NOT_READY: "The note is still being processed; try again once it is ready.",
};

// GET /v1/recording/{id}/visit-options body ->
//   { current, options, suggestedId, canMove, blockerText, postedToAthena }
// with the current visit always listed and marked, ordered by date then time.
// Tolerates the payload with or without an outer {status, data} wrapper.
export const normalizeVisitOptions = (body) => {
  const d = body?.data && !Array.isArray(body.data) ? body.data : body || {};
  const current = normOption(d.current);
  const seen = new Set();
  const options = [];
  for (const o of Array.isArray(d.options) ? d.options : []) {
    const n = normOption(o);
    if (!n || seen.has(n.appointmentid)) continue;
    seen.add(n.appointmentid);
    options.push(n);
  }
  if (current && !seen.has(current.appointmentid)) options.push(current);
  const mins = (t) => {
    const m = /^(\d{1,2}):(\d{2}) (AM|PM)$/.exec(t || "");
    if (!m) return 24 * 60;
    return ((Number(m[1]) % 12) + (m[3] === "PM" ? 12 : 0)) * 60 + Number(m[2]);
  };
  options.sort(
    (a, b) =>
      (a.dayKey || "").localeCompare(b.dayKey || "") ||
      mins(a.time) - mins(b.time) ||
      Number(a.appointmentid) - Number(b.appointmentid),
  );
  const blockers = Array.isArray(d.blockers) ? d.blockers : [];
  const hard = blockers.filter((b) => b !== "NOTE_POSTED_TO_ATHENA");
  const suggestedId = hasAppointmentId(d.suggested_appointmentid)
    ? String(Number(d.suggested_appointmentid))
    : "";
  return {
    current,
    suggestedId,
    canMove: d.can_move === undefined ? hard.length === 0 : Boolean(d.can_move),
    blockerText: hard.map((b) => BLOCKER_TEXT[b] || b).join(" "),
    postedToAthena:
      Boolean(d.recording?.posted_to_athena) || blockers.includes("NOTE_POSTED_TO_ATHENA"),
    options: options.map((o) => ({
      ...o,
      isCurrent: Boolean(current && o.appointmentid === current.appointmentid),
      onRecordingDay: o.serverRecordingDay,
      suggested: Boolean(suggestedId && o.appointmentid === suggestedId),
      // The server refuses a visit that already has a signed note (a second
      // sign-off would overwrite it in Athena).
      disabled: o.signedNoteIds.length > 0,
    })),
  };
};

// Mark the options dated the recording's day (the likely right visit), for a
// payload that does not flag them itself.
export const markRecordingDay = (normalized, note) => {
  const rec = recordingDayKey(note);
  return {
    ...normalized,
    options: (normalized?.options || []).map((o) => ({
      ...o,
      onRecordingDay: Boolean(o.onRecordingDay || (rec && o.dayKey === rec)),
    })),
  };
};

export const optionText = (o) => {
  const tags = [];
  if (o.isCurrent) tags.push("current");
  else if (o.suggested) tags.push("suggested");
  else if (o.onRecordingDay) tags.push("recording day");
  if (o.signedNoteIds?.length) tags.push("has a signed note");
  else if (o.unsignedNoteIds?.length && !o.isCurrent) tags.push("has an unsigned note");
  return `${o.label}${tags.map((t) => ` (${t})`).join("")}`;
};

// What the move needs acknowledged before it is sent (and the flags sent):
//   postedAck   - sections were already posted to the old encounter by edits
//                 (athena_posted_ack=true + a reason);
//   existingAck - the target visit already has an unsigned note
//                 (allow_existing_note=true; combine before signing).
export const moveRequirements = (opts, target, note) => ({
  postedAck: Boolean(opts?.postedToAthena || note?.emrposted),
  existingAck: Boolean(target?.unsignedNoteIds?.length),
});

// The move confirmation: which encounter sign-off will post to afterwards.
export const moveConfirmText = (note, target) => {
  const fromDay = shortDate(noteVisitDayKey(note));
  const from = fromDay ? `the ${fromDay} visit` : "its current visit";
  const toDay = shortDate(target?.dayKey) || target?.dayKey || "selected";
  const toRest = joinDot(target?.time, target?.provider);
  return [
    `Move this note to the ${toDay} visit${toRest ? ` (${toRest})` : ""}?`,
    `Sign & Post will then post it to that visit's Athena encounter instead of ${from}'s.`,
  ];
};

// Acknowledgement texts for the move dialog.
export const postedAckText = (note) => {
  const fromDay = shortDate(noteVisitDayKey(note));
  const from = fromDay ? `the ${fromDay} visit's` : "the current visit's";
  return `Sections of this note were already sent to ${from} Athena encounter by an edit. Moving the note does not remove them there: the office must correct that encounter in athenaNet.`;
};
export const existingAckText = (target) => {
  const ids = (target?.unsignedNoteIds || []).map((id) => `#${id}`).join(", ");
  return `That visit already has an unsigned note${ids ? ` (${ids})` : ""}. Combine the two before signing: a second sign-off overwrites the first in Athena.`;
};

// PUT /v1/recording/{id}/visit refusals that another acknowledgement fixes.
export const refusalNeeds = (code) => ({
  postedAck: code === "NOTE_POSTED_TO_ATHENA" || code === "REASON_REQUIRED",
  existingAck: code === "TARGET_HAS_UNSIGNED_NOTE",
});

// Success toast: the server's warnings, if any, after the move itself.
export const movedMessage = (res, target) => {
  const day = shortDate(target?.dayKey) || "selected";
  if (res && res.changed === false) return `The note is already on the ${day} visit.`;
  const warnings = (Array.isArray(res?.warnings) ? res.warnings : [])
    .map((w) => (typeof w === "string" ? w : w?.message || ""))
    .filter(Boolean);
  return [`Note moved to the ${day} visit.`, ...warnings].join(" ");
};

// Sign & Post on a note recorded on another day than its visit.
export const signMismatchText = (note) => {
  const rec = shortDate(recordingDayKey(note));
  const appt = shortDate(noteVisitDayKey(note));
  return `This note was recorded on ${rec} but is filed to the ${appt} visit. Sign & Post will post it to the ${appt} visit's Athena encounter. Use "Visit" to move it first if it belongs to another visit.\n\nSign it into the ${appt} visit anyway?`;
};

// The server supports a primary (signing) provider override when its
// daily-review rows carry the primary_provider key (null when unset).
export const supportsPrimaryOverride = (notes) =>
  (Array.isArray(notes) ? notes : []).some(
    (n) => n && Object.prototype.hasOwnProperty.call(n, "primary_provider"),
  );

// The provider the note is listed and signed under, and the visit's own
// provider (daily-review returns appointment_providername when it supports
// the override; providername is then the effective one).
export const primaryProviderName = (note) =>
  String(note?.primary_provider?.providername ?? "").trim();
export const visitProviderName = (note) =>
  String(note?.appointment_providername ?? (primaryProviderName(note) ? "" : note?.providername) ?? "").trim();

// Change-provider dialog body for PUT /v1/recording/{id}/primary-provider
// (providerid preferred by the server; providername kept for older servers).
export const primaryProviderBody = (provider) =>
  provider && String(provider.providername ?? "").trim()
    ? { providername: provider.providername, providerid: provider.providerid ?? null }
    : { clear: true };

// ---------------------------------------------------------------------------
// Provider identity (10-02: "Reshmi Adupa appears twice, same for Leanora").
//
// appointment_master holds two spellings of one provider: providername is the
// Athena display name ("LEANORA BRISSETT, NP-C") and linked_provider the short
// name ("Leanora"); the dropdowns were keyed by name, so every provider showed
// twice. BlinkScribe's canonical lists (GET /v1/provider/list and
// daily-review's providers) now carry one entry per providerid:
//   { providerid, providername: <label>, aliases: [every spelling seen] }
// and a name that is no provider's spelling stays as its own entry with
// providerid null. Notes carry providerid (the LINKED provider's id when the
// visit was reassigned) and the canonical label as providername.
//
// An older server returns one { providername, providerid } per SPELLING, and
// a reassigned linked_provider name under its row's ORIGINAL providerid, so
// there the name is the only safe key. "id mode" is on when the lists carry
// aliases; otherwise everything is keyed by name as before.

const normName = (s) => String(s ?? "").trim().toLowerCase();

// A usable providerid (a positive integer, possibly as a string).
export const hasProviderId = (v) =>
  (typeof v === "number" || typeof v === "string") && Number.isInteger(Number(v)) && Number(v) > 0;

// A providerid through the practice's id aliases (config/practice
// providerAliases, { fromId: toId }; 9928: { "16": "15" }, Leanora's second
// Athena id), as a Number; anything else unchanged. BlinkScribe's canonical
// list already folds such ids server-side (provider_master names); this
// keeps Note Review on the dashboard's ids if a list or note still carries
// the old one. A note's own providerid is never rewritten; a provider picked
// from the (merged) list carries the entry's id, as before.
export const aliasProviderId = (id, aliases) => {
  if (!hasProviderId(id) || !aliases) return id;
  const to = aliases[String(Number(id))];
  return hasProviderId(to) ? Number(to) : id;
};

// The list is the canonical (one-per-id, aliased) shape.
export const providersById = (list) =>
  Array.isArray(list) && list.some((p) => p && Array.isArray(p.aliases));

// Every spelling an entry answers to, label first, case-insensitively unique.
export const providerAliases = (p) => {
  const out = [];
  const seen = new Set();
  for (const a of [p?.providername, ...(Array.isArray(p?.aliases) ? p.aliases : [])]) {
    const s = String(a ?? "").trim();
    if (s && !seen.has(s.toLowerCase())) {
      seen.add(s.toLowerCase());
      out.push(s);
    }
  }
  return out;
};

// Stable <option> value for an entry: its id in id mode ("15"), "name:<label>"
// for an id-less entry, and the bare name in name mode (as today).
export const providerKey = (p, byId) => {
  if (!p) return "";
  if (byId && hasProviderId(p.providerid)) return String(Number(p.providerid));
  const name = String(p.providername ?? "").trim();
  if (!name) return "";
  return byId ? `name:${name}` : name;
};

// One entry per provider: by id in id mode (aliases merged, the first label
// kept), by name otherwise. Entries without a name are dropped.
// `aliases` ({ fromId: toId }): in id mode an aliased id merges into its
// target's entry (the first label kept).
export const dedupeProviders = (list, byId = providersById(list), aliases = null) => {
  const out = [];
  const index = new Map();
  for (const p of Array.isArray(list) ? list : []) {
    const label = String(p?.providername ?? "").trim();
    if (!label) continue;
    const pid = byId ? aliasProviderId(p.providerid, aliases) : p.providerid;
    const k = byId && hasProviderId(pid) ? String(Number(pid)) : `name:${label.toLowerCase()}`;
    const cur = index.get(k);
    if (!cur) {
      const entry = { providerid: hasProviderId(pid) ? Number(pid) : null, providername: label };
      if (byId) entry.aliases = providerAliases(p);
      index.set(k, entry);
      out.push(entry);
    } else if (byId) {
      cur.aliases = providerAliases({ providername: cur.providername, aliases: [...cur.aliases, ...providerAliases(p)] });
    }
  }
  return out;
};

export const providerHasName = (p, name) => {
  const n = normName(name);
  return Boolean(n) && providerAliases(p).some((a) => a.toLowerCase() === n);
};

// The list entry a { providerid, providername } reference names (a note's
// provider, a saved meta.providers item, primary_provider, discussed_with).
// Id mode: the NAME decides when it is a spelling of a listed provider, the
// id only when the name is blank or names nobody. Today's UI pairs the name
// the user picked with the providerid of whichever appointment row first
// carried it (the old GET /v1/provider/list gave "Ashritha Enukonda" the id
// of a Reshma Farahani row) and a reassigned note's linked NAME with the
// row's original id, so a disagreeing pair always means "the human named X".
// Name mode: the exact name, as today.
export const findProvider = (list, ref, byId = providersById(list), aliases = null) => {
  if (!ref) return null;
  const arr = Array.isArray(list) ? list : [];
  const n = normName(ref.providername);
  if (!byId) return n ? arr.find((p) => normName(p.providername) === n) || null : null;
  if (n) {
    const hit = arr.find((p) => providerHasName(p, n));
    if (hit) return hit;
  }
  if (hasProviderId(ref.providerid)) {
    const id = Number(aliasProviderId(ref.providerid, aliases));
    return arr.find((p) => hasProviderId(p.providerid) && Number(aliasProviderId(p.providerid, aliases)) === id) || null;
  }
  return null;
};

// The comparison key of a reference: its entry's key, else one built from
// the reference itself (so an unknown name still compares to itself).
export const refKey = (list, ref, byId = providersById(list), aliases = null) => {
  const hit = findProvider(list, ref, byId, aliases);
  if (hit) return providerKey(hit, byId);
  return providerKey(byId ? { ...ref, providerid: aliasProviderId(ref?.providerid, aliases) } : ref, byId);
};

export const sameProvider = (list, a, b, byId = providersById(list), aliases = null) => {
  const ka = refKey(list, a, byId, aliases);
  return ka !== "" && ka === refKey(list, b, byId, aliases);
};

// The canonical label for a reference (the raw name when unknown).
export const providerLabel = (list, ref, byId = providersById(list), aliases = null) =>
  findProvider(list, ref, byId, aliases)?.providername || String(ref?.providername ?? "").trim();

// The { providername, providerid } persisted in meta.providers / discussed_with
// and sent to sign-off: canonical label, id filled in when the name resolves.
export const providerRef = (list, ref, byId = providersById(list), aliases = null) => {
  if (!ref) return null;
  const hit = findProvider(list, ref, byId, aliases);
  if (hit) {
    // the listed entry decides (an id-less entry stays id-less, whatever id the reference carried)
    return { providername: hit.providername, providerid: hasProviderId(hit.providerid) ? Number(hit.providerid) : null };
  }
  return {
    providername: String(ref.providername ?? "").trim(),
    providerid: hasProviderId(ref.providerid) ? Number(ref.providerid) : null,
  };
};

// Does the note fall under the provider filter (a providerKey, or "ALL")?
// Id mode: by the note's providerid, by alias name when the note has none.
// Name mode: the exact name, as today.
// `aliases`: the note's providerid goes through the practice's id aliases.
export const noteMatchesProvider = (note, key, list, byId = providersById(list), aliases = null) => {
  if (!key || key === "ALL") return true;
  if (!byId) return String(note?.providername ?? "").trim() === key;
  if (hasProviderId(note?.providerid)) return String(Number(aliasProviderId(note.providerid, aliases))) === key;
  const entry = (Array.isArray(list) ? list : []).find((p) => providerKey(p, byId) === key);
  return entry
    ? providerHasName(entry, note?.providername)
    : key === providerKey({ providername: note?.providername }, byId);
};

// ---------------------------------------------------------------------------
// Signing a note filed to another day's visit (BlinkScribe sign-off answers
// 409 VISIT_MISMATCH unless confirm_visit_mismatch=true; the UI asks twice).

const NOT_A_DAY_VISIT = new Set(["cancelled", "canceled", "rescheduled", "no show"]);

// Before the first sign-off call, when Note Review can already see it: the
// note is filed to another day's visit and (per its visit options) the patient
// had a visit on the recording day, or the options are not known.
export const needsSignMismatchConsent = (note, opts) => {
  if (!noteVisitMismatch(note)) return false;
  if (!opts || opts.loading || opts.error || !Array.isArray(opts.options)) return true;
  return opts.options.some(
    (o) => o.onRecordingDay && !o.isCurrent && !NOT_A_DAY_VISIT.has(String(o.status || "").trim().toLowerCase()),
  );
};

// { recDay, apptDay, dayVisits: [{ time, provider }] } for the consent dialog,
// from the note (+ its visit options) or from the server's 409 body.
export const mismatchDetailsFromNote = (note, opts) => ({
  recDay: recordingDayKey(note),
  apptDay: noteVisitDayKey(note),
  dayVisits: ((opts && opts.options) || [])
    .filter((o) => o.onRecordingDay && !o.isCurrent)
    .map((o) => ({ time: o.time, provider: o.provider })),
});
export const mismatchDetailsFromRefusal = (body) => ({
  recDay: dayKeyOf(body?.recording_date),
  apptDay: dayKeyOf(body?.appointment_date ?? body?.appointment?.date),
  dayVisits: (Array.isArray(body?.recording_day_appointments) ? body.recording_day_appointments : []).map((a) => ({
    time: visitTimeLabel({ starttime: a?.time }),
    provider: String(a?.provider ?? "").trim(),
  })),
});

// Wording of the two consent steps (VisitConsentHost, mode "sign-mismatch").
export const signMismatchCopy = (d) => {
  const rec = shortDate(d?.recDay) || "another day";
  const appt = shortDate(d?.apptDay) || "another day's";
  const visits = (d?.dayVisits || []).map((v) => joinDot(v.time, v.provider)).filter(Boolean).join(", ");
  return {
    title: "Recorded on another day than its visit",
    step1: `This note was recorded on ${rec} but is filed to the ${appt} visit${
      visits ? `, and the patient had a visit on ${rec} (${visits})` : ""
    }. Sign & Post will post it into the ${appt} visit's Athena encounter. To file it to its own visit instead, cancel and use "Visit".`,
    tickLabel: `I confirm this note belongs to the ${appt} visit`,
    typeTarget: shortDate(d?.apptDay) || "",
    confirmLabel: `Sign into the ${appt} visit`,
  };
};

// ---------------------------------------------------------------------------
// Two unsigned notes on one visit: the second sign-off would overwrite the
// first in Athena (each section is replaced). Sign is blocked until they are
// combined (or one is discarded).
const NOTE_ROW_TYPES = new Set(["", "original", "combine"]);
export const unsignedSiblingIds = (notes, note) => {
  if (!note || isNoteSigned(note) || !hasAppointmentId(note.appointmentid)) return [];
  return (Array.isArray(notes) ? notes : [])
    .filter(
      (n) =>
        n &&
        n.id !== note.id &&
        !isNoteSigned(n) &&
        NOTE_ROW_TYPES.has(String(n.recording_type ?? "").trim().toLowerCase()) &&
        Number(n.appointmentid) === Number(note.appointmentid),
    )
    .map((n) => n.id);
};
export const combineFirstText = (note, ids) =>
  `Combine first: ${[note?.id, ...(ids || [])].map((i) => `#${i}`).join(" and ")} are unsigned notes of the same visit. Signing both would overwrite the first in Athena.`;

// ---------------------------------------------------------------------------
// Combine / discard from Note Review (10-07, appointment 890480: an 18 s
// add-on became a second unsigned note and Sign & Post stayed disabled with
// no way out). BlinkScribe POST /v1/recording/combine-notes merges the notes
// into one (the kept note's edits are the base); POST /v1/recording/{id}/discard
// hides one unsigned note. Shown only when daily-review says the server can.

// The note's time of day from its row timestamp ("2026-10-07T14:52:33" ->
// "2:52 PM"), "" when unknown.
export const noteTimeLabel = (note) => {
  const m = /[T ](\d{1,2}:\d{2})/.exec(String(note?.ts ?? ""));
  return m ? visitTimeLabel({ starttime: m[1] }) : "";
};

// Recording order: row time, then id.
const recordingOrder = (a, b) =>
  String(a?.ts ?? "").localeCompare(String(b?.ts ?? "")) || Number(a?.id) - Number(b?.id);

// The selected note and its unsigned siblings on the same visit, in recording
// order; [] when there is nothing to combine.
export const visitNoteGroup = (notes, note) => {
  const ids = unsignedSiblingIds(notes, note);
  if (!ids.length) return [];
  const byId = new Map((Array.isArray(notes) ? notes : []).filter(Boolean).map((n) => [n.id, n]));
  return [note, ...ids.map((i) => byId.get(i)).filter(Boolean)].sort(recordingOrder);
};

// 18 -> "18 s", 685 -> "11 min 25 s", 600 -> "10 min", 3725 -> "1 h 2 min".
export const durationLabel = (seconds) => {
  const n = Number(seconds);
  if (seconds === null || seconds === undefined || seconds === "" || !Number.isFinite(n) || n <= 0) return "";
  const t = Math.round(n);
  if (t < 60) return `${t} s`;
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = t % 60;
  if (h) return `${h} h ${m} min`;
  return sec ? `${m} min ${sec} s` : `${m} min`;
};

const recordedByVisitProvider = (n) =>
  hasProviderId(n?.recorder_providerid) &&
  hasProviderId(n?.providerid) &&
  Number(n.recorder_providerid) === Number(n.providerid);

// The note whose edits are kept: the one recorded by the visit's provider
// when exactly one is; else the longest recording; else the oldest.
export const defaultKeepId = (group) => {
  const list = Array.isArray(group) ? group.filter(Boolean) : [];
  if (!list.length) return null;
  const byProvider = list.filter(recordedByVisitProvider);
  if (byProvider.length === 1) return byProvider[0].id;
  const sorted = [...list].sort(
    (a, b) => (Number(b.recording_duration) || 0) - (Number(a.recording_duration) || 0) || recordingOrder(a, b),
  );
  return sorted[0].id;
};

// The next note to keep when the reviewer taps "change" (cycles the group).
export const nextKeepId = (group, keepId) => {
  const list = Array.isArray(group) ? group.filter(Boolean) : [];
  if (!list.length) return null;
  const i = list.findIndex((n) => n.id === keepId);
  return list[(i + 1) % list.length].id;
};

// Discard is offered for an unsigned, unposted note of a visit that keeps
// another note (the server refuses the visit's only note).
export const canDiscard = (group, note) =>
  Array.isArray(group) &&
  group.length > 1 &&
  !!note &&
  group.some((n) => n && n.id === note.id) &&
  !isNoteSigned(note) &&
  !note.emrposted;

// daily-review capabilities (absent on an older BlinkScribe: banner text only).
export const supportsCombineNotes = (res) => Boolean(res?.capabilities?.combine_notes);
export const supportsDiscardNote = (res) => Boolean(res?.capabilities?.discard_note);

// ---- Patient-id search (owner 10-19) -----------------------------------------
// A BlinkScribe with daily-review `patientid` advertises
// capabilities.patient_search: it then lists one patient's notes across all
// dates (Summarized + APPROVED, newest PATIENT_SEARCH_LIMIT). An older server
// ignores the unknown field and would send a whole day back, so the screen
// asks only when the capability is there.
export const supportsPatientSearch = (res) => Boolean(res?.capabilities?.patient_search);
export const PATIENT_SEARCH_LIMIT = 200;

// The patient id typed in the search box ("8823" or "#8823"), else null.
// Positive and within the server's INT column.
export const patientSearchId = (q) => {
  const m = /^#?\s*(\d{1,10})$/.exec(String(q ?? "").trim());
  if (!m) return null;
  const id = Number(m[1]);
  return Number.isSafeInteger(id) && id > 0 && id <= 2147483647 ? id : null;
};

// The merge a note of the group is waiting for (daily-review combine_pending).
export const combinePendingId = (group) => {
  for (const n of Array.isArray(group) ? group : []) {
    const id = Number(n?.combine_pending);
    if (Number.isInteger(id) && id > 0) return id;
  }
  return null;
};

export const combineIntroText = (group) =>
  `This visit has ${(group || []).length} unsigned notes. Combine them before signing (signing both would overwrite the first in Athena).`;

// "#48852 · recorded by Madhu Pr · 11 min 25 s · 2:40 PM"
export const noteRowText = (note) =>
  joinDot(
    `#${note?.id}`,
    String(note?.recorded_by ?? "").trim() ? `recorded by ${String(note.recorded_by).trim()}` : "",
    durationLabel(note?.recording_duration),
    noteTimeLabel(note),
  );

export const keepLineText = (group, keepId) => {
  const others = (group || []).length - 1;
  return `Keeps #${keepId}'s edits; adds the other recording${others > 1 ? "s" : ""}.`;
};

export const discardConfirmText = (note) => {
  const detail = joinDot(
    durationLabel(note?.recording_duration),
    String(note?.recorded_by ?? "").trim() ? `recorded by ${String(note.recorded_by).trim()}` : "",
    noteTimeLabel(note),
  ).replace(/ · /g, ", ");
  return `Discard note #${note?.id}${detail ? ` (${detail})` : ""}? It leaves Note Review and is never posted to Athena. Nothing is deleted; support can restore it.`;
};

// Note Review list: "recorded by <name> · <duration>" when another listed note
// is on the same visit, else "".
export const recorderLine = (notes, note) => {
  const appt = Number(note?.appointmentid);
  if (!hasAppointmentId(note?.appointmentid)) return "";
  const sibling = (Array.isArray(notes) ? notes : []).some(
    (n) => n && n.id !== note.id && Number(n.appointmentid) === appt,
  );
  if (!sibling) return "";
  const who = String(note?.recorded_by ?? "").trim();
  return joinDot(who ? `recorded by ${who}` : "", durationLabel(note?.recording_duration));
};

// A refusal / failure from the combine or discard endpoints -> text to show.
export const combineErrorText = (err, fallback) => {
  const body = err?.response?.data ?? err ?? {};
  const raw = body?.message ?? body?.detail;
  if (typeof raw === "string" && raw && raw !== "Not Found") return raw;
  const st = err?.response?.status;
  if (st === 404) return "Combining notes is not available on this server yet.";
  if (st === 403) return "You are not allowed to change these notes.";
  return fallback;
};

// category/update answered "saved in BlinkScribe only" (a note filed to
// another day's visit is never posted by an edit): the text to show, or "".
export const savedLocallyNotice = (res) =>
  res && res.saved_locally_only
    ? (typeof res.notice === "string" && res.notice) ||
      "Saved in BlinkScribe only; not sent to Athena (this note is filed to another day's visit)."
    : "";
